import clsx from 'clsx';
import { BLOCKS } from '@contentful/rich-text-types';
import { renderHeading, renderParagraph, RichTextRenderer } from '~components/RichTextRenderer';
import { ComponentLink } from '~components/ComponentLink';
import { Button } from '~components/Button';
import { useHero } from '../useHero';

import * as styles from './HeroOverlay.module.css';

import type { NodeRenderer } from '@contentful/rich-text-react-renderer';
import type { References } from '~components/ComponentTextMedia';
import type { VideoType } from '~components/Video';
import type { Hero } from '../types';

const VideoPlayer: React.FC<{ video: VideoType[] }> = ({ video }) => {
  const generateSource = () => {
    if (video.length === 2) {
      return (
        <>
          <source src={video[0].file.url} type={video[0].file.contentType} media='(max-width: 767px)' />
          <source src={video[1].file.url} type={video[1].file.contentType} media='(min-width: 768px)' />
        </>
      );
    }

    if (video.length === 3) {
      return (
        <>
          <source src={video[0].file.url} type={video[0].file.contentType} media='(max-width: 767px)' />
          <source src={video[1].file.url} type={video[1].file.contentType} media='(min-width: 768px)' />
          <source src={video[2].file.url} type={video[2].file.contentType} />
        </>
      );
    }

    if (video.length === 4) {
      return (
        <>
          <source src={video[0].file.url} type={video[0].file.contentType} media='(max-width: 767px)' />
          <source src={video[1].file.url} type={video[1].file.contentType} media='(min-width: 768px)' />
          <source src={video[2].file.url} type={video[2].file.contentType} media='(max-width: 767px)' />
          <source src={video[3].file.url} type={video[3].file.contentType} media='(min-width: 768px)' />
        </>
      );
    }

    return <source src={video[0].file.url} type={video[0].file.contentType} />;
  };

  return (
    <video className={styles.video} autoPlay loop muted playsInline preload='auto'>
      {generateSource()}
    </video>
  );
};

type HeroOverlayProps = Omit<JSX.IntrinsicElements['div'], 'content'> & {
  content: Hero;
};

export const HeroOverlay: React.FC<HeroOverlayProps> = ({ content: { video, content }, className, ...props }) => {
  const { animationState } = useHero();

  return (
    <div {...props} className={clsx(styles.heroOverlay, className)}>
      <VideoPlayer video={video} />
      {content && (
        <RichTextRenderer
          content={content.raw}
          wrapperElement={'div'}
          className={clsx(styles.content, animationState !== 'initial' && styles.hide)}
          options={{
            renderNode: {
              [BLOCKS.HEADING_1]: renderHeading(1, 1, { className: styles.h1 }),
              [BLOCKS.HEADING_2]: renderHeading(1, 2, { className: styles.h2 }),
              [BLOCKS.PARAGRAPH]: renderParagraph({ className: styles.p }),
              [BLOCKS.EMBEDDED_ENTRY]: renderEmbeddedBlockEntry(content.references),
            },
          }}
        />
      )}
    </div>
  );
};

export const renderEmbeddedBlockEntry =
  (references: References): NodeRenderer =>
  ({ data }) => {
    const entry = references.find(({ contentful_id }) => data.target.sys.id === contentful_id);
    switch (entry?.__typename) {
      case 'ContentfulComponentLink':
        return (
          <Button
            className={styles.button}
            element={<ComponentLink className={styles.link} tabIndex={0} link={entry} />}
          >
            {entry.label}
          </Button>
        );
      default:
        return null;
    }
  };
